<mat-horizontal-stepper [linear]="true" (selectionChange)="step($event)">
  <mat-step [completed]="data.valid" [editable]="!imported">
    <ng-template matStepLabel i18n>Load data</ng-template>
    <div class="page" [formGroup]="data">
      <div class="top">
        <button mat-flat-button (click)="fileInput.click()" i18n>Import file</button>
        <button mat-icon-button (click)="showHelp()" matTooltip="Column mapping help" i18n-matTooltip>
          <mat-icon>help_outline</mat-icon>
        </button>
        <input type="file" #fileInput style="display: none" accept=".csv" (change)="loadFile(fileInput.files)" />
        <mat-checkbox formControlName="header" i18n>Data includes header row</mat-checkbox>
      </div>
      <div class="fill">
        <mat-form-field>
          <mat-label i18n>Member Data</mat-label>
          <textarea formControlName="data" placeholder="Import file or paste data here" i18n-placeholder matInput></textarea>
        </mat-form-field>
      </div>
    </div>
    <div class="actions">
      <ng-container *ngTemplateOutlet="actionTemplate"></ng-container>
      <span class="spacer"></span>
      <button mat-flat-button matStepperNext [disabled]="!data.valid" i18n>Next</button>
    </div>
  </mat-step>
  <mat-step [editable]="!imported">
    <ng-template matStepLabel i18n>Map columns</ng-template>
    <div class="page">
      <div class="table-container">
        <table>
          <thead>
            <tr [formGroup]="columnsAsFormGroup">
              @for (col of columns.controls; track col; let i = $index) {
                <th>
                  <mat-form-field>
                    <mat-label>{{ header[i] }}</mat-label>
                    <mat-select [formControlName]="i">
                      @for (column of availableColumns; track column.field) {
                        <mat-option [value]="column">{{ column.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of sampleRows; track $index) {
              <tr>
                @for (cell of row; track $index) {
                  <td>{{ cell }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
    <div class="actions">
      <button mat-button (click)="addColumn()" i18n>Add column</button>
      <ng-container *ngTemplateOutlet="actionTemplate"></ng-container>
      <span class="spacer"></span>
      <button mat-button matStepperPrevious i18n>Back</button>
      <button mat-flat-button matStepperNext i18n>Next</button>
    </div>
  </mat-step>
  <mat-step [editable]="!imported">
    <ng-template matStepLabel i18n>Preview import</ng-template>
    <div class="page">
      <div class="table-container">
        <table>
          <thead>
            <tr>
              @if (hasError) {
                <th class="error" i18n>Error</th>
              }
              @for (col of usedColumns; track col.field) {
                <th>{{ col.label }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of sampleImportRows; track $index) {
              <tr>
                @if (hasError) {
                  <td class="error">{{ row.error }}</td>
                }
                @for (col of usedColumns; track col.field) {
                  <td>{{ _.get(row.data, col.field, '') }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
    <div class="actions">
      <ng-container *ngTemplateOutlet="actionTemplate"></ng-container>
      <span class="spacer"></span>
      <button mat-button matStepperPrevious i18n>Back</button>
      <button mat-flat-button matStepperNext i18n>Import</button>
    </div>
  </mat-step>
  <mat-step>
    <ng-template matStepLabel i18n>Import</ng-template>
    <div class="page">
      @if (!done) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      }
      @if (done) {
        <div class="table-container">
          <table>
            <thead>
              <tr>
                @if (hasError) {
                  <th class="error" i18n>Error</th>
                }
                @for (col of usedColumns; track col) {
                  <th>{{ col.label }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of sampleImportRows; track $index) {
                <tr>
                  @if (hasError) {
                    <td class="error">{{ row.error }}</td>
                  }
                  @for (col of usedColumns; track col) {
                    <td>{{ _.get(row.data, col.field, '') }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
      <div class="actions">
        @if (done && !hasError) {
          <span class="result" i18n>Imported {{ importRows.length }} rows</span>
        }
        @if (done && hasError) {
          <span class="result" i18n>
          Imported {{ importRows.length }} rows with {{ errorCount }} errors
        </span>
        }
        <span class="spacer"></span>
        @if (done) {
          <button mat-flat-button (click)="finished.emit(true)" i18n>Done</button>
        }
      </div>
    </div>
  </mat-step>
</mat-horizontal-stepper>

<ng-template #addColumnDialog let-data>
  <h3 matDialogTitle i18n>Add Column</h3>
  <mat-dialog-content>
    <p i18n>Add a column with static data for your import</p>
    <form #addForm="ngForm">
      <mat-form-field>
        <mat-label i18n>Column name</mat-label>
        <mat-select [(ngModel)]="data.column" name="column" required>
          @for (column of state.columns; track column.field) {
            <mat-option [value]="column">{{ column.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field>
        <mat-label i18n>Column value</mat-label>
        <input matInput [(ngModel)]="data.value" name="value" required>
      </mat-form-field>
    </form>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button mat-button mat-dialog-close i18n>Cancel</button>
    <button mat-button [disabled]="addForm.invalid" [matDialogClose]="data" i18n>Add Column</button>
  </mat-dialog-actions>
</ng-template>

<ng-template #helpDialog let-data>
  <h3 matDialogTitle i18n>Column Mapping</h3>
  <mat-dialog-content>
    <p i18n>The following columns are available for this import</p>
    <ul>
      @for (column of data.columns; track column.field) {
        <li>{{ column.label }}</li>
      }
    </ul>
    <p i18n>If your columns headers do not match the expected labels, you can map the columns manually in the next step.</p>
    <p i18n>You can also add a new column with static data for your import.</p>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button mat-button mat-dialog-close i18n>Close</button>
  </mat-dialog-actions>
</ng-template>

<ng-template #actionTemplate>
  <div class="action-toggle">
    <mat-icon matPrefix i18n-matTooltip matTooltip="Edit and Delete requires matching on a unique identifier in the reference field.">help</mat-icon>
    <mat-radio-group [(ngModel)]="state.action" class="action-group">
      <mat-radio-button value="add" matTooltip="Add meberrs" i18n-matTooltip i18n>Add</mat-radio-button>
      <mat-radio-button value="edit" matTooltip="Edit members using reference field, add non-existing members" i18n-matTooltip i18n>Add/Edit</mat-radio-button>
      <mat-radio-button class="danger" value="full" matTooltip="Add, Edit, then Delete members with reference id not in this data" i18n-matTooltip i18n>Add/Edit/Delete</mat-radio-button>
    </mat-radio-group>
  </div>
</ng-template>